/**
 * Client-side session token storage.
 *
 * Persists the session token issued by `POST /api/auth/login` so an
 * authenticated Session survives a page reload; the API client attaches it as a
 * bearer token on every request (Req 1.2, 1.6). The token is the source of
 * access — clearing it (on sign-out or a 401) returns the SPA to the Passcode
 * entry interface (Req 1.6, 1.7).
 */
import type { LoginSuccess } from "./api/types";

const STORAGE_KEY = "grain.session";

/** The stored session material. */
export interface StoredSession {
  token: string;
  sessionId: string;
  expiresAt: string;
}

/** Read the stored session, or `null` when signed out / never authenticated. */
export function loadSession(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.token === "string" &&
      typeof parsed.sessionId === "string" &&
      typeof parsed.expiresAt === "string"
    ) {
      return {
        token: parsed.token,
        sessionId: parsed.sessionId,
        expiresAt: parsed.expiresAt,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Persist the session material from a successful login. */
export function saveSession(login: LoginSuccess): void {
  const session: StoredSession = {
    token: login.token,
    sessionId: login.sessionId,
    expiresAt: login.expiresAt,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage may be unavailable (private mode); the in-memory token still
    // works for the current page lifetime via the caller holding the value.
  }
}

/** Clear the stored session (sign-out, or an authorizer 401). */
export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors on clear.
  }
}

/** True when a session token is currently stored. */
export function hasSession(): boolean {
  return loadSession() !== null;
}
